import type { Conversation } from '@volt/shared';
import { DATE_BUCKET_ORDER, dateBucket } from '@volt/shared';
import type { DateBucket } from '@volt/shared';

/**
 * Sidebar grouping: pinned conversations float to the top in their own group,
 * everything else is bucketed by {@link dateBucket} (Today / Yesterday /
 * Previous 7 days / Previous 30 days / Older). Within every group the most
 * recently active conversation comes first.
 */

export type SidebarGroupKey = 'pinned' | DateBucket;
export type SidebarLabelKey =
  | 'sidebar.pinned'
  | 'sidebar.today'
  | 'sidebar.yesterday'
  | 'sidebar.previous7Days'
  | 'sidebar.previous30Days'
  | 'sidebar.older';

export interface ConversationGroup {
  key: SidebarGroupKey;
  labelKey: SidebarLabelKey;
  conversations: Conversation[];
}

const BUCKET_LABEL_KEYS: Record<SidebarGroupKey, SidebarLabelKey> = {
  pinned: 'sidebar.pinned',
  today: 'sidebar.today',
  yesterday: 'sidebar.yesterday',
  previous7Days: 'sidebar.previous7Days',
  previous30Days: 'sidebar.previous30Days',
  older: 'sidebar.older',
};

/** The title the API assigns to a chat before its first message (see services/chat.ts makeTitle). */
export const DEFAULT_CONVERSATION_TITLE = 'New chat';

/** True when the chat still carries the server's untranslated default title. */
export function isDefaultConversationTitle(title: string | null | undefined): boolean {
  return (title ?? '').trim().toLowerCase() === DEFAULT_CONVERSATION_TITLE.toLowerCase();
}

/**
 * The title to show for a chat: the server default is rendered in the user's
 * language, anything else as stored (empty falls back to `empty`).
 */
export function displayConversationTitle(
  title: string | null | undefined,
  labels: { newTitle: string; empty: string },
): string {
  if (isDefaultConversationTitle(title)) return labels.newTitle;
  return title?.trim() ? title : labels.empty;
}

/** The timestamp a conversation is sorted/bucketed by. */
export function conversationTime(conversation: Conversation): number {
  const stamp =
    conversation.lastMessageAt ?? conversation.updatedAt ?? conversation.createdAt;
  const time = Date.parse(stamp);
  return Number.isNaN(time) ? 0 : time;
}

export function groupConversations(
  conversations: Conversation[],
  now: Date = new Date(),
  flatList = false,
): ConversationGroup[] {
  const pinned: Conversation[] = [];
  const byBucket = new Map<DateBucket, Conversation[]>();

  for (const conversation of conversations) {
    if (!flatList && conversation.isPinned) {
      pinned.push(conversation);
      continue;
    }
    const bucket = dateBucket(new Date(conversationTime(conversation)), now);
    const list = byBucket.get(bucket) ?? [];
    list.push(conversation);
    byBucket.set(bucket, list);
  }

  const byTimeDesc = (a: Conversation, b: Conversation) =>
    conversationTime(b) - conversationTime(a);

  const groups: ConversationGroup[] = [];
  if (pinned.length > 0) {
    groups.push({
      key: 'pinned',
      labelKey: BUCKET_LABEL_KEYS.pinned,
      conversations: pinned.sort(byTimeDesc),
    });
  }
  for (const bucket of DATE_BUCKET_ORDER as readonly DateBucket[]) {
    const list = byBucket.get(bucket);
    if (list && list.length > 0) {
      groups.push({
        key: bucket,
        labelKey: BUCKET_LABEL_KEYS[bucket]!,
        conversations: list.sort(byTimeDesc),
      });
    }
  }
  return groups;
}
